import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readPictures } from "@/office/word/media";

export const lektionBildEinfuegen: Lesson = {
  id: "einfuegen",
  title: "Bilder einfügen",
  summary: "Woher Bilder kommen und wie sie ins Dokument gelangen.",
  durationMinutes: 7,
  learningGoals: [
    "Bilder aus verschiedenen Quellen einfügen",
    "Die Dateigröße im Blick behalten",
    "Bilder in der richtigen Größe verwenden",
  ],
  steps: [
    {
      id: "bild-einfuegen",
      title: "Das erste Bild",
      explanation: [
        "Word bietet unter „Einfügen“ → „Bilder“ mehrere Quellen: „Dieses Gerät“ für " +
          "eigene Dateien, „Bestandsbilder“ für die mitgelieferte Sammlung und " +
          "„Onlinebilder“ für die Suche im Web.",
        "Bei Onlinebildern lohnt ein Blick auf die Lizenz. Word zeigt sie zu jedem " +
          "Ergebnis an – für ein internes Protokoll ist das meist unkritisch, für eine " +
          "Veröffentlichung dagegen entscheidend.",
        "Ein praktischer Hinweis vorweg: Ein Foto aus einer modernen Kamera bringt " +
          "mehrere Megabyte mit. Fügst du zehn davon ein, wird aus dem Word-Dokument " +
          "schnell eine Datei, die kein Postfach mehr annimmt. Dazu gleich mehr.",
      ],
      keyPoints: [
        "Einfügen → Bilder → Dieses Gerät / Bestandsbilder / Onlinebilder.",
        "Bei Onlinebildern die angezeigte Lizenz beachten.",
        "Bilder lassen sich auch per Ziehen aus dem Dateiexplorer einfügen.",
      ],
      task: {
        instruction: "Füge ein beliebiges Bild in dein Dokument ein.",
        hint: "Am schnellsten über Einfügen → Bilder → Bestandsbilder, falls du gerade keine eigene Datei zur Hand hast.",
        check: async () => {
          const bilder = await readPictures();
          if (bilder.length === 0) {
            return fail(
              "Im Dokument ist noch kein Bild eingefügt.",
              "Achte darauf, ein eingebettetes Bild einzufügen – ein Textfeld oder eine gezeichnete Form zählt hier nicht.",
            );
          }
          return pass(
            `${bilder.length} ${bilder.length === 1 ? "Bild" : "Bilder"} im Dokument gefunden.`,
          );
        },
      },
    },
    {
      id: "dateigroesse",
      title: "Bilder komprimieren",
      explanation: [
        "Word speichert eingefügte Bilder standardmäßig in voller Auflösung – auch wenn " +
          "du sie im Dokument auf Briefmarkengröße ziehst. Das Verkleinern am Bildschirm " +
          "ändert nichts an der Datenmenge dahinter.",
        "Abhilfe schafft die Komprimierung: Bild anklicken, dann unter „Bildformat“ → " +
          "„Bilder komprimieren“. Dort wählst du eine Zielauflösung und kannst festlegen, " +
          "ob nur dieses Bild oder alle im Dokument behandelt werden.",
        "Als Orientierung: 220 ppi reicht für hochwertigen Druck, 150 ppi für normale " +
          "Ausdrucke, 96 ppi für Dokumente, die nur am Bildschirm gelesen werden. " +
          "Setze außerdem den Haken bei „Zugeschnittene Bildbereiche löschen“ – " +
          "abgeschnittene Ränder bleiben sonst in der Datei erhalten und lassen sich von " +
          "Empfängern sogar wiederherstellen.",
      ],
      keyPoints: [
        "Bildformat → Bilder komprimieren.",
        "Zugeschnittene Bereiche bleiben ohne Komprimierung in der Datei enthalten.",
        "96 ppi für Bildschirm, 150–220 ppi für Druck.",
      ],
    },
  ],
};
